<template>
  <div class="ux-value-search" @keydown="handleKeydown">
    <div class="ux-value-search-selected-items" v-if="list">
      <div
        v-for="(selectedItem, index) in selectedLabels"
        :key="index"
        class="ux-value-search-selected-item"
      >
        {{ selectedItem }}
      </div>
    </div>
    <search
      v-model="keywords"
      @focus="isFocused = true"
      @blur="handleBlur"
      ref="input"
      :placeholder="placeholderText"
      :loading="searching"
      :debounce="asyncOptions ? 500 : 50"
    />
    <ux-list
      v-if="showOptions && filteredOptions.length"
      class="ux-value-search-options"
    >
      <ux-list-item
        v-for="(option, index) in filteredOptions"
        :key="option.value || index"
        :class="{ active: index === activeIndex }"
        @click="selectItem(option, true)"
        @mouseover="activeIndex = index"
        class="ux-value-search-option"
      >
        {{ option.title }}
      </ux-list-item>
    </ux-list>
  </div>
</template>

<script>
import Search from "../../form/inputs/search.vue";

export default {
  components: {
    Search,
  },
  props: {
    modelValue: {
      type: [Array, String],
      default() {
        return [];
      },
    },
    search: {
      type: String,
      default: "",
    },
    options: {
      type: Array,
      default() {
        return [];
      },
    },
    asyncOptions: {
      type: String,
    },
    serverFiltered: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    list: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      keywords: this.search,
      asyncResults: [],
      model: [],
      activeIndex: -1,
      isFocused: false,
      showOptions: false,
      searching: false,
    };
  },
  computed: {
    placeholderText() {
      if (this.placeholder) {
        return this.placeholder;
      }
      return this.multiple ? "Search to add items" : "Search for an item";
    },
    normalizedOptions() {
      return (this.options || []).map(function (opt) {
        if (typeof opt === "string") {
          return { title: opt, value: opt };
        }
        const value =
          opt.value || opt._id || opt.title || opt.name || String(opt);
        const title = opt.title || opt.name || opt.label || value;
        return { title, value };
      });
    },
    optionTitleLookup() {
      return this.normalizedOptions.reduce(function (h, o) {
        h[o.value] = o.title;
        return h;
      }, {});
    },
    filteredOptions() {
      const kw = (this.keywords || "").toLowerCase();
      if (!kw) return [];

      if (this.asyncOptions) {
        if (this.serverFiltered) {
          return this.asyncResults;
        }
        return this.asyncResults.filter(function (opt) {
          return opt.title.toLowerCase().includes(kw);
        });
      }

      return this.normalizedOptions.filter(function (opt) {
        return opt.title.toLowerCase().includes(kw);
      });
    },
    selectedLabels() {
      const lookup = this.optionTitleLookup;
      return (this.model || []).map(function (val) {
        return lookup[val] || val;
      });
    },
    totalOptions() {
      return this.filteredOptions.length;
    },
  },
  watch: {
    modelValue: {
      handler(m) {
        if (!m) {
          this.model = [];
        } else if (Array.isArray(m)) {
          this.model = m.slice();
        } else {
          this.model = [m];
        }
      },
      immediate: true,
    },
    search(newSearch) {
      this.keywords = newSearch;
    },
    keywords: {
      immediate: true,
      async handler(search) {
        this.$emit("update:search", search);

        this.asyncResults = [];
        this.showOptions = false;

        if (!search) {
          this.searching = false;
          return;
        }

        try {
          if (this.asyncOptions) {
            this.searching = true;
            const separator = this.asyncOptions.includes("?") ? "&" : "?";
            const url = `${this.asyncOptions}${separator}search=${encodeURIComponent(search)}`;
            const { data } = await this.$sdk.api.get(url);
            this.asyncResults = (data || []).map(function (item) {
              if (typeof item === "string") {
                return { title: item, value: item };
              }
              const value =
                item.value ||
                item._id ||
                item.title ||
                item.name ||
                String(item);
              const title = item.title || item.name || item.label || value;
              return { title, value };
            });
          }

          this.showOptions = true;
          this.activeIndex = this.totalOptions > 0 ? 0 : -1;
        } catch (err) {
          // Silently handle fetch errors
        } finally {
          this.searching = false;
        }
      },
    },
    filteredOptions() {
      this.activeIndex = this.totalOptions > 0 ? 0 : -1;
    },
  },
  methods: {
    focus() {
      this.$refs.input.focus();
    },
    dispatch() {
      this.$emit("update:modelValue", this.model);
    },
    handleKeydown(event) {
      if (!this.showOptions || this.totalOptions === 0) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          this.activeIndex = (this.activeIndex + 1) % this.totalOptions;
          this.$nextTick(() => {
            const activeElement = this.$el.querySelector(
              ".ux-value-search-option.active",
            );
            if (activeElement) {
              activeElement.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
              });
            }
          });
          break;
        case "ArrowUp":
          event.preventDefault();
          this.activeIndex =
            this.activeIndex <= 0
              ? this.totalOptions - 1
              : this.activeIndex - 1;
          this.$nextTick(() => {
            const activeElement = this.$el.querySelector(
              ".ux-value-search-option.active",
            );
            if (activeElement) {
              activeElement.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
              });
            }
          });
          break;
        case "Enter":
          event.preventDefault();
          if (
            this.activeIndex >= 0 &&
            this.activeIndex < this.filteredOptions.length
          ) {
            this.selectItem(this.filteredOptions[this.activeIndex], true);
          }
          break;
        case "Escape":
          this.showOptions = false;
          this.$refs.input?.$el?.blur();
          break;
      }
    },
    selectItem(option, shouldRefocus = false) {
      const value = option.value;
      const isAlreadySelected = this.model.indexOf(value) !== -1;

      if (!isAlreadySelected) {
        if (!this.multiple) {
          this.model = [value];
        } else {
          this.model.push(value);
        }
        this.dispatch();
      }

      this.keywords = "";
      this.showOptions = false;

      if (shouldRefocus) {
        this.$nextTick(() => {
          this.$refs.input?.$el?.focus();
        });
      }
    },
    handleBlur() {
      setTimeout(() => {
        this.isFocused = false;
        this.showOptions = false;
      }, 200);
    },
  },
  mounted() {
    this.handleClickOutside = (event) => {
      if (this.$el && !this.$el.contains(event.target)) {
        this.showOptions = false;
      }
    };
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>

<style lang="scss" scoped>
.ux-value-search {
  position: relative;
}

.ux-value-search-selected-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.ux-value-search-selected-item {
  background-color: #f0f0f0;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
}

.ux-value-search-options {
  position: absolute;
  max-height: 20vh;
  overflow-y: auto;
  z-index: 100;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid color-mix(in lab, currentColor, var(--bg1));
  border-radius: 4px;
  margin-top: 4px;
  background-color: var(--bg1);
}

.ux-value-search-option.active {
  background-color: color-mix(in lab, var(--primary) 15%, var(--bg1));
}
</style>
